import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/webcall';
import { getErrorMessage } from '../../utils/utils';
import AccessController from '../../components/access_controller';
import { getVizTokens } from '../analytics/vizTokens';
import { EmptyState } from '../analytics/components';
import { REQUEST_STATUS, dateRange, money, shortDate } from './shared';

/**
 * Leave and advances waiting on an owner.
 *
 * Deciding is deliberately two steps rather than an approve button on the card:
 * approving an advance moves money and approving leave changes someone's pay,
 * and both let the owner grant something other than what was asked for. The
 * dialog is where that choice is made explicit.
 */

function DecisionDialog(props) {
    const { request, decision, onClose, onDecided, showErrorMessage } = props;
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const approving = decision === 'approve';
    const isLeave = request?.kind === 'leave';

    const [leaveType, setLeaveType] = useState('paid');
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!request) return;
        setNote('');
        setLeaveType(request.leave_type || 'unpaid');
        setAmount(request.amount ? String(Number(request.amount)) : '');
    }, [request]);

    // Approving more than was asked for is a slip, not a decision. The server
    // refuses it too; this stops the owner reaching for the button at all.
    const tooMuch = approving && !isLeave && request
        && Number(amount) > Number(request.amount);

    const withinAllowance = Boolean(
        request?.allowance
        && Number(amount || request.amount) <= Number(request.allowance.available));

    const submit = () => {
        setSaving(true);
        const payload = { decision, note };
        if (approving && isLeave) payload.leave_type = leaveType;
        if (approving && !isLeave) payload.amount = amount;

        api.post(`/staff/requests/${request.id}/decide/`, payload)
            .then((res) => onDecided(res.data))
            .catch((error) => showErrorMessage(getErrorMessage(error)))
            .finally(() => setSaving(false));
    };

    if (!request) return null;

    return (
        <Dialog open onClose={saving ? undefined : onClose}
            fullWidth maxWidth="xs" fullScreen={isMobile}>
            <DialogTitle>
                {approving ? 'Approve' : 'Turn down'} — {request.staff_name}
            </DialogTitle>
            <DialogContent>
                <Alert severity={approving ? 'info' : 'warning'} sx={{ mb: 2 }}>
                    <AlertTitle>
                        {isLeave
                            ? `${request.days} day${request.days === 1 ? '' : 's'}, ${dateRange(request.start_date, request.end_date)}`
                            : `${money(request.amount)} requested`}
                    </AlertTitle>
                    {request.reason || 'No reason given.'}
                </Alert>

                {/* Whether what they asked for is within what they have earned.
                    This is the whole judgement, so it is put in front of the
                    owner before they decide - the person asking never sees it,
                    which is why they can ask for anything. */}
                {!isLeave && request.allowance && (
                    <Alert severity={withinAllowance ? 'success' : 'warning'}
                        sx={{ mb: 2 }}>
                        {withinAllowance ? (
                            <>Within their allowance
                            of <strong>{money(request.allowance.available)}</strong> &mdash;{' '}
                            {request.allowance.available_days} of
                            the {request.allowance.worked_days} days worked since
                            their last payment, with {request.allowance.hold_days} held
                            back.</>
                        ) : (
                            <>Over their allowance
                            of <strong>{money(request.allowance.available)}</strong> &mdash;{' '}
                            {request.allowance.available_days} of
                            the {request.allowance.worked_days} days worked since
                            their last payment, with {request.allowance.hold_days} held
                            back. You can still approve it, or approve part.</>
                        )}
                    </Alert>
                )}

                {approving && isLeave && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="grant-type">Approve as</InputLabel>
                        <Select labelId="grant-type" label="Approve as"
                            value={leaveType} onChange={(e) => setLeaveType(e.target.value)}>
                            <MenuItem value="paid">Paid leave — no deduction</MenuItem>
                            <MenuItem value="unpaid">
                                Unpaid leave — deducted pro rata
                            </MenuItem>
                        </Select>
                    </FormControl>
                )}

                {approving && !isLeave && (
                    // Deliberately type="text" with a numeric keypad, not
                    // type="number". A number input cannot be selected by
                    // triple-click and changes value on a stray scroll wheel -
                    // both of which turn 6,000 into a figure nobody intended on
                    // a field that hands out money. Text selects normally, and
                    // the value is validated here and again on the server.
                    <TextField label="Amount to approve" fullWidth
                        sx={{ mb: 2 }} value={amount}
                        inputMode="decimal"
                        onChange={(e) => setAmount(
                            e.target.value.replace(/[^\d.]/g, ''))}
                        onFocus={(e) => e.target.select()}
                        error={tooMuch}
                        helperText={tooMuch
                            ? `More than the ${money(request.amount)} asked for.`
                            : 'You can approve less than was asked for. It is '
                              + 'recovered from their payslips until it is cleared.'} />
                )}

                <TextField label={approving ? 'Note (optional)' : 'Reason (optional)'}
                    fullWidth multiline minRows={2} value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={approving ? '' : 'Too busy that week'} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" color={approving ? 'primary' : 'error'}
                    onClick={submit} disabled={saving || tooMuch}>
                    {saving ? 'Saving...' : approving ? 'Approve' : 'Turn down'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function Approvals(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const tokens = useMemo(() => getVizTokens(theme.palette.mode === 'dark'),
        [theme.palette.mode]);
    const navigate = useNavigate();

    const [requests, setRequests] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [filter, setFilter] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [deciding, setDeciding] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const params = filter === 'all' ? {} : { status: filter };
        api.get('/staff/requests/', { params }).then((res) => {
            setRequests(res.data.requests || []);
            setPendingCount(res.data.pending_count || 0);
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => setLoading(false));
    }, [filter, showErrorMessage]);

    useEffect(() => { load(); }, [load]);

    const onDecided = (data) => {
        setDeciding(null);
        // Warnings carry the arithmetic an owner needs and a staff member must
        // not see - leave balances among them - so they surface here and are
        // never written into the decision note the person reads.
        const warnings = data.warnings || [];
        if (warnings.length) {
            // A warning is not a failure, but it must not be swallowed - it says
            // the approval did something other than the obvious.
            showErrorMessage(warnings.join(' '));
        } else {
            showSuccessMessage(
                data.request.status === 'approved' ? 'Approved' : 'Turned down');
        }
        load();
    };

    return (
        <AccessController require="owner">
            {deciding && (
                <DecisionDialog request={deciding.request} decision={deciding.decision}
                    onClose={() => setDeciding(null)} onDecided={onDecided}
                    showErrorMessage={showErrorMessage} />
            )}

            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <IconButton aria-label="Back to staff" onClick={() => navigate('/staff')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>Approvals</Typography>
                        <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                            {pendingCount === 0
                                ? 'Nothing waiting on you'
                                : `${pendingCount} waiting on you`}
                        </Typography>
                    </Box>
                </Box>

                <ToggleButtonGroup exclusive size="small" value={filter} sx={{ mb: 2 }}
                    onChange={(event, value) => value && setFilter(value)}>
                    <ToggleButton value="pending">Waiting</ToggleButton>
                    <ToggleButton value="approved">Approved</ToggleButton>
                    <ToggleButton value="rejected">Turned down</ToggleButton>
                    <ToggleButton value="all">All</ToggleButton>
                </ToggleButtonGroup>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : requests.length === 0 ? (
                    <Paper variant="outlined">
                        <EmptyState title="Nothing here" tokens={tokens}>
                            {filter === 'pending'
                                ? 'No one is waiting on a decision.'
                                : 'No requests with that status.'}
                        </EmptyState>
                    </Paper>
                ) : (
                    <Grid container spacing={1.5}>
                        {requests.map((request) => {
                            const state = REQUEST_STATUS[request.status] || {};
                            const isLeave = request.kind === 'leave';
                            return (
                                <Grid size={{ xs: 12, md: 6 }} key={request.id}>
                                    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                                        <Box sx={{ display: 'flex', gap: 1,
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start' }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1"
                                                    sx={{ fontWeight: 600 }}>
                                                    {request.staff_name}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {isLeave
                                                        ? `${request.days} day${request.days === 1 ? '' : 's'} ${request.leave_type || ''} leave`
                                                        : `${money(request.amount)} advance`}
                                                </Typography>
                                                <Typography variant="caption"
                                                    sx={{ color: tokens.textMuted }}>
                                                    {isLeave
                                                        ? dateRange(request.start_date, request.end_date)
                                                        : `asked ${shortDate(request.created_at?.slice(0, 10))}`}
                                                </Typography>
                                            </Box>
                                            <Chip size="small" label={state.label || request.status}
                                                color={state.colour}
                                                variant={request.is_open ? 'outlined' : 'filled'} />
                                        </Box>

                                        {request.reason && (
                                            <Typography variant="body2"
                                                sx={{ mt: 1, color: tokens.textSecondary }}>
                                                {request.reason}
                                            </Typography>
                                        )}

                                        {request.is_open ? (
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1.5,
                                                flexDirection: isMobile ? 'column' : 'row' }}>
                                                <Button variant="contained" size="small"
                                                    fullWidth={isMobile}
                                                    onClick={() => setDeciding({ request, decision: 'approve' })}>
                                                    Approve
                                                </Button>
                                                <Button variant="outlined" size="small"
                                                    color="error" fullWidth={isMobile}
                                                    onClick={() => setDeciding({ request, decision: 'reject' })}>
                                                    Turn down
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Typography variant="caption"
                                                sx={{ display: 'block', mt: 1,
                                                    color: tokens.textMuted }}>
                                                {state.label}
                                                {request.decided_by_name
                                                    ? ` by ${request.decided_by_name}` : ''}
                                                {request.decision_note
                                                    ? ` — “${request.decision_note}”` : ''}
                                            </Typography>
                                        )}
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        </AccessController>
    );
}

export default Approvals;
